import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import type { Me } from "../api";
import { useAuth } from "../auth";
import { BookmarkRuler } from "../bookmark";
import { ErrorNote, JobBadge, Spinner, useLoad } from "../ui";
import { WikiContext, useBookmark, wikiHref } from "./common";

const TYPES: { type: string; label: string }[] = [
  { type: "character", label: "Personages" },
  { type: "place", label: "Plaatsen" },
  { type: "object", label: "Voorwerpen" },
  { type: "group", label: "Groepen" },
];

export function WikiLayout({ me }: { me: Me }) {
  const { bookId = "" } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const book = useLoad(() => api.book(bookId), [api, bookId]);
  const [bookmark, setBookmark] = useBookmark(book.data);
  const recap = useLoad(
    () =>
      book.data && book.data.last_chapter_index !== null
        ? api.recap(bookId, bookmark)
        : Promise.resolve([]),
    [api, bookId, bookmark, book.data],
  );
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const first = book.data?.first_chapter_index ?? 0;
  const last = book.data?.last_chapter_index ?? 0;

  // Keyboard: "/" search, "[" and "]" previous/next chapter.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "[" && bookmark > first) setBookmark(bookmark - 1);
      else if (e.key === "]" && bookmark < last) setBookmark(bookmark + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bookmark, first, last, setBookmark]);

  if (book.error) return <ErrorNote error={book.error} />;
  if (!book.data) return <Spinner />;
  const b = book.data;
  if (b.last_chapter_index === null) {
    return (
      <div className="stack">
        <h1>{b.title}</h1>
        <p className="muted">
          De wiki is beschikbaar zodra het boek is verwerkt.{" "}
          <JobBadge status={b.job_status} stage={b.job_stage} notBefore={b.job_not_before} />
        </p>
      </div>
    );
  }
  const current = recap.data?.find((c) => c.chapter_index === bookmark);
  const isOwner = b.owner_id === me.id;
  const href = (sub: string) => wikiHref(b.id, sub, bookmark);

  return (
    <WikiContext.Provider value={{ book: b, bookmark, isOwner }}>
      <div className="wiki">
        <header className="wiki-head">
          <button className="btn btn-ghost wiki-menu" onClick={() => setMenuOpen((o) => !o)}
                  aria-expanded={menuOpen} aria-label="Menu">
            ☰
          </button>
          <Link className="wiki-title" to={href("")}>
            {b.title}
          </Link>
          <form
            className="wiki-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim())
                navigate(wikiHref(b.id, "/zoeken", bookmark, `&q=${encodeURIComponent(query.trim())}`));
            }}
          >
            <input
              ref={searchRef}
              type="search"
              placeholder="Zoek in de wiki… ( / )"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Zoeken in de wiki"
            />
          </form>
        </header>

        <BookmarkRuler
          first={first}
          last={last}
          value={bookmark}
          onChange={setBookmark}
          current={current?.title}
        />

        <div className="wiki-body">
          <nav className={`wiki-nav ${menuOpen ? "wiki-nav-open" : ""}`} aria-label="Wiki"
               onClick={() => setMenuOpen(false)}>
            <NavLink end to={href("")}>Hoofdpagina</NavLink>
            {TYPES.map((t) => (
              <NavLink key={t.type} to={href(`/c/${t.type}`)}>{t.label}</NavLink>
            ))}
            <NavLink to={href(`/h/${bookmark}`)}>Dit hoofdstuk</NavLink>
            <NavLink to={href("/tijdlijn")}>Tijdlijn</NavLink>
            <NavLink to={href("/kaart")}>Relatiekaart</NavLink>
            <NavLink to={href("/nieuw")}>Wat is er nieuw</NavLink>
            <hr />
            <Link to={`/books/${b.id}`}>Overzicht{isOwner ? " & controleren" : ""}</Link>
            {isOwner && <ExportLink bookId={b.id} bookmark={bookmark} />}
          </nav>
          <main className="wiki-main">
            <Outlet />
          </main>
        </div>
      </div>
    </WikiContext.Provider>
  );
}

function ExportLink({ bookId, bookmark }: { bookId: string; bookmark: number }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const blob = await api.wikiExport(bookId, bookmark);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `wiki-t-m-H${bookmark}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="link" disabled={busy} onClick={() => void download()}
            title="Statische wiki (MkDocs / Obsidian) t/m dit hoofdstuk">
      {busy ? "Exporteren…" : "Exporteer (Markdown)"}
    </button>
  );
}
